import * as THREE from "three";
import { useEffect, useRef } from "react";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader";

export default function WritableBoardItem({
  sceneRef,
  cameraRef,
  objectsRef,
  selectedObjectRef,
  url,
  position = [0, 0, 0],
  rotation = [0, 0, 0],
  scale,
  heightAboveFloor,
  writableMeshName,
  textureLoader,
}) {
  const localModelRef = useRef(null);
  const hasLoadedRef = useRef(false);

  // LOAD MODEL
  useEffect(() => {
    if (!sceneRef?.current || hasLoadedRef.current) return;
    hasLoadedRef.current = true;

    const loader = new GLTFLoader();
    loader.load(
      url,
      (gltf) => {
        const rawModel = gltf.scene;

        const wrapper = new THREE.Group();
        localModelRef.current = wrapper;

        rawModel.scale.set(...scale);

        rawModel.updateMatrixWorld(true);

        const box = new THREE.Box3().setFromObject(rawModel);

        rawModel.position.y -= box.min.y;

        wrapper.add(rawModel);

        wrapper.position.set(
          position[0],
          position[1] + heightAboveFloor,
          position[2],
        );

        wrapper.rotation.set(...rotation);

        const axes = new THREE.AxesHelper(0.5);
        wrapper.add(axes);

        // WYSZUKIWANIE writableMesh

        let writableMesh = null;

        rawModel.traverse((child) => {
          if (child.isMesh) {
            child.userData.isSelectable = true;
            if (child.name === writableMeshName) writableMesh = child;
          }
        });

        if (!writableMesh) {
          console.warn(`Mesh ${writableMeshName} not found`);
          return;
        }

        // CANVAS TEXTURE
        const canvas = document.createElement("canvas");
        canvas.width = 1024;
        canvas.height = 512;
        const ctx = canvas.getContext("2d");

        const clearCanvas = () => {
          ctx.fillStyle = "#1e1e1e";
          ctx.fillRect(0, 0, canvas.width, canvas.height);
        };

        clearCanvas();

        const texture = new THREE.CanvasTexture(canvas);
        texture.wrapS = THREE.RepeatWrapping;
        texture.repeat.x = -1;
        texture.offset.x = 1;

        writableMesh.material = new THREE.MeshStandardMaterial({
          map: texture,
        });

        writableMesh.userData = { canvas, ctx, texture, clearCanvas };

        wrapper.userData = {
          isModel: true,
          isWritableBoard: true,
          lines: [""],
          writableMeshName,
          isSelectable: true,
          isMovable: true,
          mode: "move",
        };

        sceneRef.current.add(wrapper);
        objectsRef.current.push(wrapper);
        selectedObjectRef.current = wrapper;

        console.log("Loaded writable board:", wrapper);

        createRadialMenu(wrapper);
      },
      undefined,
      (err) => console.error("GLTF load error:", err),
    );
  }, [sceneRef, url]);

  // KEYBOARD WRITING
  useEffect(() => {
    const handleKeyDown = (e) => {
      const model = selectedObjectRef.current;
      if (!model?.userData?.isWritableBoard) return;
      if (model.userData.mode !== "write") return;

      e.stopPropagation();

      const writableMesh = model.getObjectByName(
        model.userData.writableMeshName,
      );

      console.log("SCALE:", writableMesh.scale);
      if (!writableMesh) return;

      const { canvas, ctx, texture, clearCanvas } = writableMesh.userData;
      const lines = model.userData.lines;

      if (!canvas || !ctx || !texture || !lines || !clearCanvas) return;

      if (e.key === "Backspace") {
        let last = lines[lines.length - 1];
        if (last.length > 0) lines[lines.length - 1] = last.slice(0, -1);
        else if (lines.length > 1) lines.pop();
      } else if (e.key === "Enter") {
        lines.push("");
      } else if (e.key.length === 1) {
        lines[lines.length - 1] += e.key;
      } else return;

      clearCanvas();

      ctx.fillStyle = "white";
      ctx.font = "48px Arial";
      ctx.textAlign = "left";
      ctx.textBaseline = "top";

      lines.forEach((line, i) => {
        ctx.fillText(line, 40, 40 + i * 70);
      });

      texture.needsUpdate = true;

      console.log("Updated board lines:", lines);
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [selectedObjectRef]);

  // HIGHLIGHT

  useEffect(() => {
    const interval = setInterval(() => {
      const model = localModelRef.current;
      if (!model) return;

      const writableMesh = model.getObjectByName(writableMeshName);
      if (!writableMesh) return;

      if (
        selectedObjectRef.current === model &&
        model.userData.mode === "write"
      ) {
        writableMesh.material.emissive.set(0x4da3ff);
        writableMesh.material.emissiveIntensity = 0.3;
      } else {
        writableMesh.material.emissive.set(0x000000);
        writableMesh.material.emissiveIntensity = 0;
      }
    }, 100);

    return () => clearInterval(interval);
  }, [selectedObjectRef, writableMeshName]);

  // CREATE RADIAL MENU
  const createRadialMenu = (object) => {
    if (!sceneRef.current) return;
    if (object.userData.radialMenu) {
      sceneRef.current.remove(object.userData.radialMenu);
      object.userData.radialMenu = null;
    }

    const radius = 120;
    const group = new THREE.Group();
    group.userData.isRadialMenu = true;

    const actions = [
      {
        fn: () => {
          object.userData.mode = "write";
          object.userData.isMovable = false;
          console.log("WRITE mode ON");
        },
        icon: "/icons/write.png",
      },
      {
        fn: () => {
          object.userData.mode = "move";
          object.userData.isMovable = true;
          console.log("MOVE mode ON");
        },
        icon: "/icons/move.png",
      },
    ];

    actions.forEach((action, i) => {
      const angle = (i / actions.length) * Math.PI * 2;

      const geometry = new THREE.CircleGeometry(20, 32);
      const material = new THREE.MeshBasicMaterial({
        color: 0x6cc6d9,
        depthTest: false,
      });
      const mesh = new THREE.Mesh(geometry, material);
      mesh.rotation.x = -Math.PI / 2;
      mesh.position.set(Math.cos(angle) * radius, 1, Math.sin(angle) * radius);

      // ikona
      if (textureLoader) {
        const texture = textureLoader.load(action.icon);
        const iconGeo = new THREE.PlaneGeometry(30, 30);
        const iconMat = new THREE.MeshBasicMaterial({
          map: texture,
          transparent: true,
          depthTest: false,
          side: THREE.DoubleSide,
        });
        const iconMesh = new THREE.Mesh(iconGeo, iconMat);
        iconMesh.rotation.x = Math.PI;
        iconMesh.rotation.z = -Math.PI / 2;
        iconMesh.position.set(0, 0.1, 0);
        mesh.add(iconMesh);
      }

      mesh.userData.onClick = action.fn;
      mesh.userData.isRadialButton = true;

      group.add(mesh);
    });

    sceneRef.current.add(group);
    group.position.copy(object.position);
    group.position.y = 1;

    object.userData.radialMenu = group;
  };
}

// import * as THREE from "three";
// import { useEffect, useRef, useState } from "react";
// import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader";

// export default function WritableBoardItem({
//   sceneRef,
//   objectsRef,
//   selectedObjectRef,
//   rendererRef,
//   cameraRef,
//   url,
//   position = [0, 0, 0],
//   rotation = [0, 0, 0],
//   scale = [1, 1, 1],
//   heightAboveFloor = 50,
//   writableMeshName,
// }) {
//   const localModelRef = useRef(null);
//   const dragPlane = useRef(new THREE.Plane());
//   const dragRaycaster = useRef(new THREE.Raycaster());
//   const isDraggingRef = useRef(false);

//   const [selected, setSelected] = useState(false);
//   const [mode, setMode] = useState("write"); // move / write

//   // --- Load model ---
//   useEffect(() => {
//     if (!sceneRef?.current || !rendererRef?.current || !cameraRef?.current)
//       return;

//     const loader = new GLTFLoader();
//     let model;

//     loader.load(
//       url,
//       (gltf) => {
//         model = gltf.scene;
//         localModelRef.current = model;

//         model.scale.set(...scale);
//         const box = new THREE.Box3().setFromObject(model);
//         const size = new THREE.Vector3();
//         box.getSize(size);
//         model.position.set(
//           position[0],
//           position[1] + size.y / 2 + heightAboveFloor,
//           position[2],
//         );
//         model.rotation.set(...rotation);

//         // Mesh do pisania
//         let writableMesh = null;
//         model.traverse((child) => {
//           if (child.isMesh) {
//             child.userData.isSelectable = true;
//             if (child.name === writableMeshName) writableMesh = child;
//           }
//         });

//         if (!writableMesh) {
//           console.warn(`⚠️ Mesh '${writableMeshName}' not found!`);
//           return;
//         }

//         // Canvas do pisania
//         const canvas = document.createElement("canvas");
//         canvas.width = 1024;
//         canvas.height = 512;
//         const ctx = canvas.getContext("2d");
//         ctx.fillStyle = "#1e1e1e";
//         ctx.fillRect(0, 0, canvas.width, canvas.height);
//         ctx.fillStyle = "white";
//         ctx.font = "48px Arial";

//         const texture = new THREE.CanvasTexture(canvas);
//         writableMesh.material = new THREE.MeshStandardMaterial({
//           map: texture,
//         });
//         writableMesh.userData = { canvas, ctx, texture };

//         model.userData = {
//           isWritableBoard: true,
//           lines: [""],
//           writableMeshName,
//           isSelectable: true,
//           isMovable: true,
//         };

//         sceneRef.current.add(model);
//         objectsRef.current.push(model);
//       },
//       undefined,
//       (err) => console.error("❌ Error loading GLTF:", err),
//     );

//     return () => {
//       if (model && sceneRef.current) {
//         sceneRef.current.remove(model);
//         objectsRef.current = objectsRef.current.filter((obj) => obj !== model);
//       }
//     };
//   }, [
//     sceneRef,
//     objectsRef,
//     url,
//     position,
//     rotation,
//     scale,
//     heightAboveFloor,
//     writableMeshName,
//   ]);

//   // --- Pointer events ---
//   useEffect(() => {
//     if (!rendererRef?.current || !cameraRef?.current || !localModelRef.current)
//       return;

//     const dom = rendererRef.current.domElement;

//     function pointerDown(event) {
//       if (!localModelRef.current) return;

//       const rect = dom.getBoundingClientRect();
//       const mouse = new THREE.Vector2(
//         ((event.clientX - rect.left) / rect.width) * 2 - 1,
//         -((event.clientY - rect.top) / rect.height) * 2 + 1,
//       );

//       const raycaster = new THREE.Raycaster();
//       raycaster.setFromCamera(mouse, cameraRef.current);

//       // Sprawdzamy wszystkie meshe w modelu
//       const allMeshes = [];
//       localModelRef.current.traverse((child) => {
//         if (child.isMesh) allMeshes.push(child);
//       });

//       const intersects = raycaster.intersectObjects(allMeshes, true);
//       if (!intersects.length) {
//         setSelected(false);
//         return;
//       }

//       setSelected(true);
//       selectedObjectRef.current = localModelRef.current;

//       // Start drag jeśli w trybie move
//       if (mode === "move") {
//         isDraggingRef.current = true;
//         dragPlane.current.set(
//           new THREE.Plane(
//             new THREE.Vector3(0, 1, 0),
//             -localModelRef.current.position.y,
//           ),
//         );
//         dragRaycaster.current = raycaster;
//       }
//     }

//     function pointerMove(event) {
//       if (!isDraggingRef.current || mode !== "move") return;

//       const rect = dom.getBoundingClientRect();
//       const mouse = new THREE.Vector2(
//         ((event.clientX - rect.left) / rect.width) * 2 - 1,
//         -((event.clientY - rect.top) / rect.height) * 2 + 1,
//       );
//       dragRaycaster.current.setFromCamera(mouse, cameraRef.current);

//       const intersectPoint = new THREE.Vector3();
//       dragRaycaster.current.ray.intersectPlane(
//         dragPlane.current,
//         intersectPoint,
//       );
//       if (!intersectPoint) return;

//       localModelRef.current.position.x = intersectPoint.x;
//       localModelRef.current.position.z = intersectPoint.z;
//     }

//     function pointerUp() {
//       isDraggingRef.current = false;
//     }

//     dom.addEventListener("pointerdown", pointerDown);
//     dom.addEventListener("pointermove", pointerMove);
//     dom.addEventListener("pointerup", pointerUp);

//     return () => {
//       dom.removeEventListener("pointerdown", pointerDown);
//       dom.removeEventListener("pointermove", pointerMove);
//       dom.removeEventListener("pointerup", pointerUp);
//     };
//   }, [rendererRef, cameraRef, mode, selectedObjectRef]);

//   // --- Write ---
//   useEffect(() => {
//     function handleKeyDown(event) {
//       if (!selected || mode !== "write") return;
//       const selectedObj = selectedObjectRef?.current;
//       if (!selectedObj || !selectedObj.userData?.isWritableBoard) return;

//       const writableMesh = selectedObj.getObjectByName(
//         selectedObj.userData.writableMeshName,
//       );
//       if (!writableMesh) return;

//       const { canvas, ctx, texture } = writableMesh.userData;
//       const lines = selectedObj.userData.lines;
//       if (!canvas || !ctx || !texture || !lines) return;

//       if (event.key === "Backspace") {
//         let last = lines[lines.length - 1];
//         if (last.length > 0) lines[lines.length - 1] = last.slice(0, -1);
//         else if (lines.length > 1) lines.pop();
//       } else if (event.key === "Enter") {
//         lines.push("");
//       } else if (event.key.length === 1) {
//         lines[lines.length - 1] += event.key;
//       } else return;

//       ctx.fillStyle = "#1e1e1e";
//       ctx.fillRect(0, 0, canvas.width, canvas.height);
//       ctx.fillStyle = "white";
//       ctx.font = "48px Arial";
//       lines.forEach((line, i) => ctx.fillText(line, 40, 70 + i * 70));
//       texture.needsUpdate = true;
//     }

//     window.addEventListener("keydown", handleKeyDown);
//     return () => window.removeEventListener("keydown", handleKeyDown);
//   }, [mode, selected, selectedObjectRef]);

//   // --- UI ---
//   return selected ? (
//     <div
//       style={{
//         position: "absolute",
//         top: 10,
//         left: 10,
//         zIndex: 100,
//         background: "rgba(0,0,0,0.3)",
//         padding: "5px",
//         borderRadius: "5px",
//       }}
//     >
//       <button onClick={() => setMode("move")} style={{ marginRight: 5 }}>
//         Move
//       </button>
//       <button onClick={() => setMode("write")}>Write</button>
//       <div>Mode: {mode}</div>
//     </div>
//   ) : null;
// }

export function DebugBoardMeshes({ url }) {
  useEffect(() => {
    const loader = new GLTFLoader();
    loader.load(
      url,
      (gltf) => {
        const model = gltf.scene;
        console.log("📦 GLTF loaded:", model);

        console.log("🔹 Meshes in the model:");
        model.traverse((child) => {
          if (child.isMesh) {
            console.log("Mesh name:", child.name);
          }
        });
      },
      undefined,
      (err) => {
        console.error("❌ Error loading GLTF:", err);
      },
    );
  }, [url]);

  return null;
}
